import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { bumpReloadSeq } from "@/app/_store/slices/calendarUiSlice";
import type { ApiOk } from "@/app/types/api";
import type {
  CreateServiceBookingInput,
  ServiceBookingDto,
  ServiceBookingListItemDto,
  ServiceBookingStatus,
} from "@/app/types/serviceBookings";

export const serviceBookingsApi = createApi({
  reducerPath: "serviceBookingsApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/service-bookings" }),
  tagTypes: ["ServiceBookings"],
  endpoints: (builder) => ({
    getAllServiceBookings: builder.query<
      any[],
      { timelineStart?: string; timelineEnd?: string; animalSpeciesId?: number } | void
    >({
      query: (params) =>
        params ? { url: "", method: "GET", params: { ...params } } : { url: "", method: "GET" },
      transformResponse: (response: ApiOk<any[]>) =>
        Array.isArray(response.data) ? response.data : [],
      providesTags: [{ type: "ServiceBookings", id: "LIST" }],
    }),
    getServiceBookingsByEvent: builder.query<ServiceBookingListItemDto[], number>({
      query: (eventId) => ({ url: `?eventId=${eventId}`, method: "GET" }),
      transformResponse: (response: ApiOk<ServiceBookingListItemDto[]>) => response.data,
      providesTags: (_result, _error, eventId) => [
        { type: "ServiceBookings", id: "LIST" },
        { type: "ServiceBookings", id: `EVENT_${eventId}` },
      ],
    }),
    getServiceBookingsByCustomer: builder.query<any[], number | string>({
      query: (customerId) => ({ url: `bycustomer/${customerId}`, method: "GET" }),
      transformResponse: (response: ApiOk<{ bookings: any[] }>) =>
        response.data?.bookings ?? [],
      providesTags: (_result, _error, customerId) => [
        { type: "ServiceBookings", id: "LIST" },
        { type: "ServiceBookings", id: `CUSTOMER_${customerId}` },
      ],
    }),
    createServiceBooking: builder.mutation<ServiceBookingDto, CreateServiceBookingInput>({
      query: (body) => ({ url: "", method: "POST", body }),
      transformResponse: (response: ApiOk<ServiceBookingDto>) => response.data,
      invalidatesTags: (_result, _error, arg) => [
        { type: "ServiceBookings", id: "LIST" },
        { type: "ServiceBookings", id: `EVENT_${arg.eventId}` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),
    updateServiceBookingStatus: builder.mutation<
      ServiceBookingDto,
      { id: number; status: ServiceBookingStatus }
    >({
      query: ({ id, status }) => ({ url: `/${id}`, method: "PATCH", body: { status } }),
      transformResponse: (response: ApiOk<ServiceBookingDto>) => response.data,
      invalidatesTags: [{ type: "ServiceBookings", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),
  }),
});

export const {
  useGetAllServiceBookingsQuery,
  useCreateServiceBookingMutation,
  useGetServiceBookingsByEventQuery,
  useGetServiceBookingsByCustomerQuery,
  useUpdateServiceBookingStatusMutation,
} = serviceBookingsApi;
